
import { useState } from "react";
import { format } from "date-fns";
import { ChevronRight, Image as ImageIcon, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Idea } from "./IdeaCard";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

interface HistoryItemProps {
  id: string;
  imageUrl: string;
  ideas: Idea[];
  timestamp: Date;
  onDelete: (historyId: string) => Promise<void>;
}

export function HistoryItem({ id, imageUrl, ideas, timestamp, onDelete }: HistoryItemProps) {
  const [expanded, setExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(id);
      toast.success("History entry deleted successfully");
    } catch (error) {
      console.error('❌ Error deleting history entry:', error);
      toast.error("Failed to delete history entry");
    } finally {
      setIsDeleting(false);
    }
  };
  
  return (
    <div className="glass-card rounded-xl mb-4 overflow-hidden transition-all duration-300">
      <div className="flex items-center p-3 gap-3">
        {/* Image + Content Section - Clickable for expand/collapse */}
        <div 
          className="flex items-center cursor-pointer flex-1 min-w-0"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="h-16 w-16 rounded-lg overflow-hidden mr-3 flex-shrink-0 bg-muted">
            {imageUrl ? (
              <img 
                src={imageUrl} 
                alt="Captured objects" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageIcon className="text-muted-foreground" size={24} />
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-center">
              <h3 className="font-medium truncate">
                {ideas.length} creative {ideas.length === 1 ? "idea" : "ideas"}
              </h3>
              <span className="text-xs text-muted-foreground ml-2">
                {format(timestamp, "MMM d, p")}
              </span>
            </div>
            
            <p className="text-sm text-muted-foreground truncate">
              {ideas.map(idea => idea.title).join(", ")}
            </p>
          </div>
        </div>

        {/* Expand/Collapse Arrow */}
        <div 
          className="flex items-center cursor-pointer p-1 hover:bg-muted/50 rounded-md transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <ChevronRight 
            size={18} 
            className={cn(
              "text-muted-foreground transition-transform", 
              expanded && "rotate-90"
            )} 
          />
        </div>

        {/* Delete Button Section - Separated and Prominent */}
        <div className="flex items-center border-l border-border pl-3">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10 border border-border/50 hover:border-destructive/30 transition-all"
                disabled={isDeleting}
              >
                <Trash2 size={18} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete History Entry</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this history entry? This will permanently remove all {ideas.length} associated {ideas.length === 1 ? 'idea' : 'ideas'} and cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      
      {expanded && (
        <div className="px-3 pb-3 animate-fade-in-up">
          <div className="border-t border-border pt-3">
            <div className="space-y-2">
              {ideas.slice(0, 2).map((idea, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div>
                    <h4 className="text-sm font-medium">{idea.title}</h4>
                    <p className="text-xs text-muted-foreground">
                      {idea.description.slice(0, 60)}...
                    </p>
                  </div>
                </div>
              ))}
              
              {ideas.length > 2 && (
                <p className="text-xs text-muted-foreground">
                  +{ideas.length - 2} more {ideas.length - 2 === 1 ? "idea" : "ideas"}
                </p>
              )}
            </div>
            
            <Link 
              to={`/results/${id}`}
              className="inline-block w-full text-center text-primary text-sm mt-3 py-2 border border-primary/30 rounded-lg hover:bg-primary/5 transition-colors"
            >
              View Details
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default HistoryItem;
